/**
 * The feeds this app knows how to receive, with the copy Admin → Directories → Feeds shows.
 *
 * Mirror of functions/src/feeds/registry.js (functions/ deploys standalone and cannot import
 * from web/). Keep the ids, `columnMapped` flags and status-doc names identical: the admin
 * page reads the status documents this file names, and the settings validator only accepts
 * the feed ids the server copy lists.
 *
 * Two shapes of feed exist deliberately. A FIXED-IMPORTER feed (roster) runs real domain
 * logic over a known export, so its columns belong in code. A COLUMN-MAPPED feed (clients)
 * copies an arbitrary export into records the app can look up, so its columns belong in
 * configuration — the firm knows what the export contains, and a developer does not.
 */

export const FEEDS = [
  {
    id: 'roster',
    label: 'Roster (people)',
    columnMapped: false,
    logCollection: 'rosterFeedLog',
    pollStatusDoc: 'rosterFeedPollStatus',
    defaultSubjectMatch: 'NMMExport',
    runCallable: 'adminRunRosterFeedPoll',
    clearClaimCallable: 'adminClearRosterFeedClaim',
    description:
      'The daily people export. Its columns are fixed in code because the import does real work with them — repairing unquoted names, matching practice groups by text, deactivating people who fall off the export. Managed from Admin → Roster.',
    managedElsewhere: 'Admin → Roster',
  },
  {
    id: 'clients',
    label: 'Client directory',
    columnMapped: true,
    logCollection: 'clientFeedLog',
    pollStatusDoc: 'clientFeedPollStatus',
    defaultSubjectMatch: 'ClientExport',
    runCallable: 'adminRunClientFeedPoll',
    clearClaimCallable: 'adminClearClientFeedClaim',
    description:
      'Clients, so “new matter for an existing client” can pick one instead of retyping it. Map the export’s columns below: which one is the client number, which is the name, and — for any other column — which form field it should fill in when a client is chosen.',
  },
];

/**
 * What to do with a column. This is the whole vocabulary of a feed's mapping — an administrator
 * chooses one of these per column and, for anything kept, optionally a form field to fill in.
 *
 * `ignore` matters more than it looks: an export with forty columns of which eight are useful
 * should not be storing the other thirty-two, and saying so explicitly reads better than a
 * half-configured row.
 */
export const COLUMN_ROLES = [
  ['number', 'Client number (searchable, and the record key)'],
  ['name', 'Client name (searchable)'],
  ['status', 'Active / inactive flag'],
  ['data', 'Store it (can fill in a form field)'],
  ['ignore', 'Ignore this column'],
];

/**
 * The per-column clean-ups, mirroring functions/src/feeds/clientImport.js COLUMN_TRANSFORMS.
 *
 * Labelled by what the person mapping the export can actually see in it, rather than by what
 * the importer does internally: someone looking at a column full of "ATTN: ..." should be able
 * to find the option without knowing the word "addressee".
 */
export const COLUMN_TRANSFORMS = [
  ['', 'None'],
  ['firstEmail', 'Several emails in one cell — keep the first'],
  ['street', 'Address line: the street'],
  ['addressee', 'Address line: the ATTN / C-O line'],
];

export function feedById(id) {
  return FEEDS.find((feed) => feed.id === id) || null;
}

/** Mirrors registry.js CUSTOM_FEED_ID — the ids an administrator may create. */
export const CUSTOM_FEED_ID = /^[a-z][a-z0-9_]{2,39}$/;

/**
 * Every feed the admin page should show: the built-ins, plus whatever an administrator has
 * added to settings/feeds. A custom feed gets the same shape as a built-in one so the page has
 * no special cases; its per-feed Firestore names are derived from its id exactly as the server
 * derives them (registry.js customFeedDefaults).
 */
export function allFeeds(storedFeeds) {
  const stored = storedFeeds && typeof storedFeeds === 'object' ? storedFeeds : {};
  const builtInIds = new Set(FEEDS.map((feed) => feed.id));
  const custom = Object.keys(stored)
    .filter((id) => !builtInIds.has(id) && CUSTOM_FEED_ID.test(id))
    .sort()
    .map((id) => ({
      id,
      label: (stored[id] && stored[id].label) || id,
      columnMapped: true,
      custom: true,
      logCollection: 'feedLog_' + id,
      pollStatusDoc: 'feedPollStatus_' + id,
      defaultSubjectMatch: id,
      runCallable: 'adminRunClientFeedPoll',
      clearClaimCallable: 'adminClearClientFeedClaim',
      description:
        'A directory you added. Records land in directories/' +
        id +
        '/records and can be picked on the form with a "Client (from a directory)" field pointed at this feed.',
    }));
  return [...FEEDS, ...custom];
}

/** Where a feed's records live — mirrors registry.js feedRecordsPath. */
export function feedRecordsPath(feedId) {
  return feedId === 'clients' ? ['clients'] : ['directories', feedId, 'records'];
}

/**
 * A stable, valid storage key suggested from a CSV header — the same normalization the
 * validator enforces (starts with a letter, letters/numbers/underscore only). Offered as a
 * default so mapping a discovered column is one click rather than an exercise in guessing
 * what the validator will accept.
 */
export function suggestColumnKey(header) {
  const words = headerWords(header).filter((word) => /^[a-z]/.test(word) || words0(word));
  const camel = words
    .map((word, index) => (index === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join('')
    .replace(/^[^a-z]+/, '');
  return camel.slice(0, 48) || 'column';
}

/** A leading digit cannot start a key, but a digit-containing word is fine after the first. */
function words0(word) {
  return /^[0-9]/.test(word) === false;
}

/** A heading broken into lower-case words, splitting on punctuation AND case boundaries. */
function headerWords(header) {
  return String(header || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * A first guess at what a column is for, from its heading.
 *
 * This exists because "which column is the client number?" turned out to be the step people get
 * stuck on: the feed discovers the real headings, refuses to guess, and reports that its mapping
 * is incomplete — all correct, and all useless if nobody realises a choice is waiting for them.
 * Pre-selecting the obvious ones turns the mapping from a blank form into a confirmation.
 *
 * A GUESS, never a decision: it only ever pre-fills an unsaved row, it is shown in the dropdown
 * like any other choice, and nothing imports until an administrator saves. Deliberately narrow —
 * an identifier-ish or name-ish or status-ish heading, and everything else is plain data — because
 * a confident wrong guess is worse than an obvious blank.
 */
export function suggestColumnRole(header, { taken = [] } = {}) {
  const has = (role) => taken.includes(role);
  // Word-boundary matching on a normalized heading, so `client_Department` is not caught by a
  // loose /name/ test. Case boundaries count as separators too: real headings mix conventions in
  // one file — `CLIENT_CODE` beside `ClientStatus` beside `client_PracticeGroup` — and splitting
  // only on punctuation would leave "clientstatus" as one unrecognizable word.
  const wordSet = new Set(headerWords(header));

  const looksLikeNumber =
    wordSet.has('code') ||
    wordSet.has('number') ||
    wordSet.has('no') ||
    wordSet.has('num') ||
    wordSet.has('id');
  if (looksLikeNumber && !has('number')) return 'number';

  if (wordSet.has('name') && !has('name')) return 'name';

  if ((wordSet.has('status') || wordSet.has('active') || wordSet.has('inactive')) && !has('status')) {
    return 'status';
  }
  return 'data';
}

/**
 * Turns discovered headings into mapping rows, with a suggested role for each. Roles are assigned
 * left to right so the FIRST identifier-ish column wins, matching how these exports are laid out.
 */
export function suggestColumns(headers) {
  const taken = [];
  return (Array.isArray(headers) ? headers : []).map((header) => {
    const role = suggestColumnRole(header, { taken });
    if (role !== 'data') taken.push(role);
    return { header, key: suggestColumnKey(header), role, target: '' };
  });
}

/**
 * Which of a feed's columns fill in a form field when a record is chosen, in the order the
 * admin listed them. Shared by the admin page's summary and by deriveMatter's fill, so the
 * page cannot claim a mapping the form does not actually apply.
 */
/**
 * Naming a feed as an auto-fill rule's `lookupIn`.
 *
 * Prefixed rather than bare, and the server validator insists on it: a custom feed id may be any
 * lowercase word, so a bare one is indistinguishable from a misspelled directory name — which
 * would validate and then quietly find nothing. See adminActions.js validateFieldDerivations.
 */
export const feedLookupId = (feedId) => `feed:${feedId}`;

/** The feed id back out of a `lookupIn`, or '' when it names a plain directory. */
export const lookupFeedId = (lookupIn) =>
  typeof lookupIn === 'string' && lookupIn.startsWith('feed:') ? lookupIn.slice(5) : '';

/**
 * The feeds an auto-fill rule can look a record up in — those with a column mapping to read.
 *
 * A feed with no columns mapped yet is left out: offering it would let someone author a rule
 * whose attribute list is empty, which can only be saved as a rule that does nothing.
 */
export function feedLookupDirectories(storedFeeds) {
  const stored = storedFeeds && typeof storedFeeds === 'object' ? storedFeeds : {};
  return allFeeds(stored)
    .filter((feed) => feed.columnMapped)
    .filter((feed) => Array.isArray(stored[feed.id]?.columns) && stored[feed.id].columns.length)
    .map((feed) => [feedLookupId(feed.id), feed.label]);
}

/**
 * What a rule can copy out of one of a feed's records: the record's own number and name, and
 * every column the import actually stores.
 *
 * Read from the feed's OWN mapping rather than a curated list, because that mapping is the only
 * thing that knows what this firm's export contains — and it is already how the columns are
 * named on screen, so "City" here is the same "City" the person set up over there. An ignored
 * column is not offered: nothing is stored under it, so a rule reading it would silently blank
 * its target on every selection.
 */
export function feedLookupAttributes(storedFeeds, feedId) {
  const columns = Array.isArray(storedFeeds?.[feedId]?.columns) ? storedFeeds[feedId].columns : [];
  const seen = new Set(['clientNumber', 'clientName']);
  const fromColumns = [];
  for (const column of columns) {
    if (!column || !column.key || column.role === 'ignore') continue;
    if (column.role === 'number' || column.role === 'name') continue;
    if (seen.has(column.key)) continue;
    seen.add(column.key);
    fromColumns.push([column.key, column.label || column.header || column.key]);
  }
  return [
    ['clientNumber', 'Record number'],
    ['clientName', 'Record name'],
    ...fromColumns,
  ];
}

export function mappedTargets(feedSettings) {
  const columns = Array.isArray(feedSettings?.columns) ? feedSettings.columns : [];
  return columns
    .filter((column) => column && column.key && typeof column.target === 'string' && column.target.trim())
    .map((column) => ({ key: column.key, target: column.target.trim(), label: column.label || column.header }));
}
